<template>
  <div class="page-container monitor-page">
    <el-breadcrumb class="page-breadcrumb" separator="/">
      <el-breadcrumb-item>数据管理</el-breadcrumb-item>
      <el-breadcrumb-item>数据报表</el-breadcrumb-item>
      <el-breadcrumb-item>用户合约交易与资产监控表</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="page-header">
      <h2>用户合约交易与资产监控表</h2>
      <p>查看用户账户资产、交易行为、资金流入与返佣贡献</p>
    </div>

    <el-tabs
      v-model="activeTab"
      class="summary-tabs"
      @tab-click="handleTabChange"
    >
      <el-tab-pane label="时间段汇总" name="summary" />
      <el-tab-pane label="每日明细" name="daily" />
    </el-tabs>

    <section class="filter-panel">
      <div class="filter-row">
        <div class="filter-item uid-filter">
          <!--          <label>UID</label>-->
          <el-input
            v-model.trim="filters.uid"
            placeholder="请输入UID"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </div>
        <div class="filter-item">
          <!--          <label>合伙人UID</label>-->
          <el-input
            v-model.trim="filters.partnerUid"
            placeholder="请输入合伙人UID"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </div>
        <div class="filter-item user-type-filter">
          <!--          <label>用户类型</label>-->
          <el-select v-model="filters.userType" placeholder="全部" clearable>
            <el-option label="普通用户" :value="2" />
            <el-option label="模拟用户" :value="1" />
          </el-select>
        </div>
        <div class="filter-item date-filter">
          <!--          <label>统计时间</label>-->
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期（必填）"
            end-placeholder="结束日期（必填）"
            :clearable="false"
            unlink-panels
          />
        </div>
        <div class="filter-actions">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </div>
        <el-button
          class="filter-export"
          type="primary"
          plain
          :loading="exporting"
          @click="handleExport"
          >导出数据</el-button
        >
      </div>
    </section>

    <div class="caliber-banner">
      <span class="caliber-tag"><i class="el-icon-info" /> 统计口径</span>
      <span>{{ caliberDescription }}</span>
    </div>

    <section class="table-panel">
      <div class="table-panel-header">
        <div>
          <h3>{{ tableTitle }}</h3>
          <p>
            共 {{ activeColumns.length }} 个字段 · 当前展示
            {{ visibleColumns.length }} 个
          </p>
        </div>
        <div class="table-actions">
          <el-button icon="el-icon-setting" @click="columnDialogVisible = true"
            >列表字段</el-button
          >
        </div>
      </div>

      <el-table
        :key="activeTab + '-' + columnVersion"
        ref="summaryTable"
        v-loading="loading"
        :data="rows"
        class="summary-table"
        height="calc(100vh - 495px)"
        border
      >
        <el-table-column
          v-for="(column, columnIndex) in visibleColumns"
          :key="column.prop"
          :fixed="columnIndex < (activeTab === 'daily' ? 2 : 1) ? 'left' : false"
          :prop="column.prop"
          :label="column.label"
          :min-width="column.minWidth"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <button
              v-if="isDetailClickable(scope.row, column)"
              type="button"
              class="detail-link"
              @click="openDetail(scope.row, column.detailType)"
            >
              {{ formatCell(scope.row[column.prop], column) }}
            </button>
            <span
              v-else
              :class="mainValueClass(scope.row[column.prop], column)"
            >
              {{ formatCell(scope.row[column.prop], column) }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ totalCount }} 条</span>
        <el-pagination
          :current-page.sync="pagination.page"
          :page-size="pagination.size"
          :page-sizes="[10, 20, 50, 100]"
          :total="totalCount"
          layout="sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </section>

    <column-settings-dialog
      :visible.sync="columnDialogVisible"
      :columns="activeColumns"
      :page-label="activeTab === 'daily' ? '明细页' : '汇总页'"
      @save="handleSaveColumns"
    />
    <detail-dialog
      :visible.sync="detailVisible"
      :uid="currentUid"
      :config="currentDetailConfig"
      :items="currentDetailItems"
      :loading="detailLoading"
    />
  </div>
</template>

<script>
import Decimal from "decimal.js";
import ColumnSettingsDialog from "./components/ColumnSettingsDialog";
import DetailDialog from "./components/DetailDialog";
import api from "./api";
import {
  COLUMN_STORAGE_KEY,
  DAILY_COLUMN_STORAGE_KEY,
  DAILY_TABLE_COLUMNS,
  restoreColumns,
  saveColumns,
} from "./columns";
import { MOCK_DETAILS } from "./mock";

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  return [formatLocalDate(start), formatLocalDate(end)];
}

function formatDateTime(value) {
  const text = String(value);
  if (/^\d+$/.test(text)) {
    const date = new Date(Number(text));
    if (!Number.isNaN(date.getTime())) {
      const time = [date.getHours(), date.getMinutes(), date.getSeconds()]
        .map((item) => String(item).padStart(2, "0"))
        .join(":");
      return `${formatLocalDate(date)} ${time}`;
    }
  }
  return text.replace("T", " ").slice(0, 19);
}

const DETAIL_CONFIGS = {
  spotBalance: {
    title: "现货余额明细",
    source: "现货账户余额（USDT）",
    columns: [
      { prop: "coin", label: "币种" },
      { prop: "balance", label: "余额", type: "amount" },
    ],
    tip: "仅展示余额大于 0 的币种；余额直接截取保留 8 位小数。",
  },
  deposit: {
    title: "充值明细",
    source: "充值金额（USDT）",
    columns: [
      { prop: "time", label: "充值时间", minWidth: 190, type: "datetime" },
      { prop: "coin", label: "币种" },
      { prop: "transactionType", label: "充值类型", type: "depositType" },
      { prop: "quantity", label: "充值数量", type: "amount" },
      { prop: "usdtAmount", label: "折U金额", type: "amount" },
    ],
    tip: "统计已确认的链上充值与内部转账；数量及折U金额直接截取保留 8 位小数。",
  },
  withdraw: {
    title: "提现明细",
    source: "提现金额（USDT）",
    columns: [
      { prop: "time", label: "提现时间", minWidth: 190, type: "datetime" },
      { prop: "coin", label: "币种" },
      { prop: "transactionType", label: "提现类型", type: "withdrawType" },
      { prop: "quantity", label: "提现数量", type: "amount" },
      { prop: "usdtAmount", label: "折U金额", type: "amount" },
    ],
    tip: "统计提现成功的链上提现与内部转账；数量及折U金额直接截取保留 8 位小数。",
  },
  netDeposit: {
    title: "净充值明细",
    source: "净充值金额（USDT）",
    columns: [
      { prop: "coin", label: "币种" },
      { prop: "quantity", label: "净充值数量", type: "amount" },
      { prop: "usdtAmount", label: "折U金额", type: "amount" },
    ],
    tip: "仅展示存在充值或提现记录的币种；净充值数量 = 充值数量 + 提现数量，并按对应汇率折算为 USDT。",
  },
  contributionRebate: {
    title: "贡献返佣明细",
    source: "总贡献返佣金额（USDT）",
    columns: [
      { prop: "partnerUid", label: "合伙人UID" },
      { prop: "rebateRate", label: "合伙人返佣比例", type: "ratio" },
      { prop: "contributionRate", label: "贡献返佣比例", type: "ratio" },
      { prop: "contributionFee", label: "贡献手续费（USDT）", type: "amount" },
      {
        prop: "contributionRebate",
        label: "贡献返佣金额（USDT）",
        type: "amount",
      },
    ],
    tip: "每个合伙人一条记录；贡献手续费是各层共用的返佣计算基数，不可跨行累加。贡献返佣比例为所选时间段内按手续费加权后的实际比例，贡献返佣金额 = 贡献手续费 × 贡献返佣比例。",
  },
  subordinateContribution: {
    title: "下级贡献返佣明细",
    source: "下级贡献返佣金额（USDT）",
    columns: [
      { prop: "subordinateUid", label: "下级用户UID", minWidth: 140 },
      { prop: "currentPartnerUid", label: "当前合伙人UID", minWidth: 150 },
      {
        prop: "currentPartnerRate",
        label: "当前合伙人返佣比例",
        minWidth: 170,
        type: "ratio",
      },
      { prop: "subordinatePartnerUid", label: "下级合伙人UID", minWidth: 150 },
      {
        prop: "subordinatePartnerRate",
        label: "下级合伙人返佣比例",
        minWidth: 170,
        type: "ratio",
      },
      {
        prop: "contributionFee",
        label: "贡献手续费（USDT）",
        minWidth: 165,
        type: "amount",
      },
      {
        prop: "contributionRebate",
        label: "贡献返佣金额（USDT）",
        minWidth: 175,
        type: "amount",
      },
    ],
    tip: "每个产生贡献返佣的下级用户一条记录；无下级合伙人时相关字段展示“--”。",
  },
};

export default {
  name: "UserContractAssetMonitor",
  components: { ColumnSettingsDialog, DetailDialog },
  data() {
    return {
      activeTab: "summary",
      loading: false,
      exporting: false,
      filters: {
        uid: "",
        partnerUid: "",
        userType: "",
        dateRange: defaultDateRange(),
      },
      rows: [],
      totalCount: 0,
      summaryColumns: restoreColumns(),
      dailyColumns: restoreColumns(
        DAILY_COLUMN_STORAGE_KEY,
        DAILY_TABLE_COLUMNS,
      ),
      pagination: { page: 1, size: 10 },
      columnDialogVisible: false,
      columnVersion: 0,
      detailVisible: false,
      detailLoading: false,
      detailRequestId: 0,
      currentUid: "",
      currentDetailType: "spotBalance",
      currentDetailItems: [],
    };
  },
  computed: {
    activeColumns() {
      return this.activeTab === "daily"
        ? this.dailyColumns
        : this.summaryColumns;
    },
    visibleColumns() {
      return this.activeColumns.filter((column) => column.visible);
    },
    tableTitle() {
      return this.activeTab === "daily" ? "用户每日明细列表" : "用户汇总列表";
    },
    caliberDescription() {
      return this.activeTab === "daily"
        ? "每个用户每个统计日期一条数据；全部20个字段横向完整展示"
        : "每个用户一条时间段汇总数据；全部30个字段可按需展示";
    },
    currentDetailConfig() {
      return (
        DETAIL_CONFIGS[this.currentDetailType] || DETAIL_CONFIGS.spotBalance
      );
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    buildListQuery(page = this.pagination.page, size = this.pagination.size) {
      const [startDate, endDate] = this.filters.dateRange || [];
      return {
        uid: this.filters.uid || null,
        partnerUid: this.filters.partnerUid || null,
        userType: this.filters.userType || null,
        startDate: startDate || null,
        endDate: endDate || null,
        page,
        size,
      };
    },
    buildExportQuery() {
      return {
        ...this.buildListQuery(),
        // 后端按 prop 白名单生成 Excel，顺序与当前页面列顺序一致。
        exportFields: this.visibleColumns.map((column) => column.prop),
      };
    },
    validateDateRange() {
      const dateRange = this.filters.dateRange;
      if (
        Array.isArray(dateRange) &&
        dateRange.length === 2 &&
        dateRange[0] &&
        dateRange[1]
      ) {
        return true;
      }
      this.$message.warning("请选择完整的统计时间");
      return false;
    },
    async loadData() {
      if (!this.validateDateRange()) return;
      const requestedTab = this.activeTab;
      this.loading = true;
      try {
        const request =
          requestedTab === "daily" ? api.getDailyList : api.getSummaryList;
        const res = await request(this.buildListQuery());
        if (requestedTab === this.activeTab && res.code === 0) {
          const data = res.data || {};
          this.rows = Array.isArray(data.list) ? data.list : [];
          this.totalCount = Number(data.totalCount) || 0;
          this.refreshTableLayout();
        }
      } catch (error) {
        if (requestedTab === this.activeTab) {
          this.rows = [];
          this.totalCount = 0;
        }
      } finally {
        if (requestedTab === this.activeTab) this.loading = false;
      }
    },
    handleSearch() {
      if (!this.validateDateRange()) return;
      this.pagination.page = 1;
      this.loadData();
    },
    handleReset() {
      this.filters = {
        uid: "",
        partnerUid: "",
        userType: "",
        dateRange: defaultDateRange(),
      };
      this.handleSearch();
    },
    handleSizeChange(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.loadData();
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.loadData();
    },
    handleSaveColumns(columns) {
      const nextColumns = columns.map((column) => ({ ...column }));
      if (this.activeTab === "daily") {
        this.dailyColumns = nextColumns;
        saveColumns(nextColumns, DAILY_COLUMN_STORAGE_KEY);
      } else {
        this.summaryColumns = nextColumns;
        saveColumns(nextColumns, COLUMN_STORAGE_KEY);
      }
      // Element UI 会缓存 el-table-column，变更key可确保保存后立即按新顺序重新注册列。
      this.columnVersion += 1;
      this.$message.success("列表字段已保存");
      this.refreshTableLayout();
    },
    handleTabChange() {
      this.pagination.page = 1;
      this.rows = [];
      this.totalCount = 0;
      this.$nextTick(() => {
        this.loadData();
        this.refreshTableLayout();
      });
    },
    refreshTableLayout() {
      this.$nextTick(() => {
        const table = this.$refs.summaryTable;
        if (table && typeof table.doLayout === "function") table.doLayout();
      });
    },
    async openDetail(row, type) {
      const requestId = ++this.detailRequestId;
      const remoteDetailTypes = [
        "spotBalance",
        "deposit",
        "withdraw",
        "netDeposit",
        "contributionRebate",
        "subordinateContribution",
      ];
      this.currentUid = row.uid;
      this.currentDetailType = type;
      this.currentDetailItems = [];
      this.detailLoading = remoteDetailTypes.includes(type);
      this.detailVisible = true;
      if (this.detailLoading) {
        const [startDate, endDate] = this.filters.dateRange || [];
        try {
          const requestMap = {
            spotBalance: api.getSpotBalanceDetails,
            deposit: api.getDepositDetails,
            withdraw: api.getWithdrawDetails,
            netDeposit: api.getNetDepositDetails,
            contributionRebate: api.getContributionRebateDetails,
            subordinateContribution: api.getSubordinateContributionDetails,
          };
          const request = requestMap[type];
          const res = await request({
            uid: row.uid,
            startDate: startDate || null,
            endDate: endDate || null,
          });
          if (requestId === this.detailRequestId && res.code === 0) {
            this.currentDetailItems = Array.isArray(res.data) ? res.data : [];
          }
        } catch (error) {
          if (requestId === this.detailRequestId) this.currentDetailItems = [];
        } finally {
          if (requestId === this.detailRequestId) this.detailLoading = false;
        }
        return;
      }
      // 其他明细将在对应后台接口完成后逐项替换。
      this.currentDetailItems = (MOCK_DETAILS[type] || []).map((item) => ({
        ...item,
      }));
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
    isDetailClickable(row, column) {
      if (!column.detailType || !this.hasValue(row[column.prop])) return false;
      if (column.detailType === "spotBalance")
        return Number(row[column.prop]) > 0;
      return Number(row[column.prop]) !== 0;
    },
    formatCell(value, column) {
      if (!this.hasValue(value)) return "--";
      if (column.type === "amount") {
        return new Decimal(value)
          .toDecimalPlaces(4, Decimal.ROUND_DOWN)
          .toString();
      }
      if (column.type === "rate") {
        return `${new Decimal(value)
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
          .toString()}%`;
      }
      if (column.type === "ratio") {
        const ratio = new Decimal(value);
        const percent = ratio.lte(1) ? ratio.mul(100) : ratio;
        return `${percent
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
          .toString()}%`;
      }
      if (column.type === "decimal") {
        return new Decimal(value)
          .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
          .toString();
      }
      if (column.type === "userType")
        return Number(value) === 1 ? "模拟用户" : "普通用户";
      if (column.type === "kycStatus")
        return Number(value) > 0 ? "已认证" : "未认证";
      if (column.type === "riskStatus")
        return value === true || Number(value) === 1 ? "已风控" : "未风控";
      if (column.type === "date") return formatDateTime(value).slice(0, 10);
      if (column.type === "datetime") return formatDateTime(value);
      return value;
    },
    mainValueClass(value, column) {
      return column.type === "amount" && Number(value) < 0
        ? "amount-negative"
        : "";
    },
    async handleExport() {
      if (!this.validateDateRange()) return;
      if (!this.visibleColumns.length) {
        this.$message.warning("请至少选择一个列表字段后再导出");
        return;
      }
      this.exporting = true;
      try {
        const daily = this.activeTab === "daily";
        const request = daily ? api.exportDaily : api.exportSummary;
        const data = await request(this.buildExportQuery());
        const blob =
          data instanceof Blob
            ? data
            : new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
        const now = new Date();
        const timestamp = `${formatLocalDate(now).replace(/-/g, "")}${String(
          now.getHours(),
        ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}${String(
          now.getSeconds(),
        ).padStart(2, "0")}`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const dataType = daily ? "每日明细数据" : "时间段汇总数据";
        link.download = `用户合约交易与资产监控表${dataType}${timestamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.monitor-page {
  box-sizing: border-box;
  min-width: 1040px;
  padding: 18px;
  overflow: auto;
  background: #f5f7fa;
}

.page-breadcrumb {
  flex: none;
  margin-bottom: 28px;
}

.page-header {
  flex: none;
  margin: 0 18px 18px;

  h2 {
    margin: 0 0 4px;
    color: #1f2a3d;
    font-size: 26px;
    line-height: 36px;
  }

  p {
    margin: 0;
    color: #7e8a9d;
  }
}

.summary-tabs {
  flex: none;
  margin: 0 18px;
}

.filter-panel,
.table-panel {
  flex: none;
  padding: 20px;
  background: #fff;
  border: 1px solid #e2e7ef;
  border-radius: 10px;
}

.filter-panel {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.filter-item {
  width: 180px;

  label {
    display: block;
    margin-bottom: 9px;
    color: #586579;
    font-size: 13px;
  }
}

.uid-filter {
  width: 220px;
}

.user-type-filter {
  width: 150px;
}

.date-filter {
  width: 330px;
}

.filter-actions {
  display: flex;
  padding-bottom: 1px;
}

.filter-export {
  margin-left: auto;
}

.caliber-banner {
  display: flex;
  flex: none;
  align-items: center;
  min-height: 38px;
  padding: 0 10px;
  margin-bottom: 16px;
  color: #77849a;
  font-size: 12px;
  background: #f1f5ff;
  border: 1px solid #d6e2ff;
  border-radius: 7px;
}

.caliber-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  margin-right: 14px;
  color: #fff;
  font-size: 12px;
  background: #2f5ee5;
  border-radius: 18px;
  cursor: help;
}

.table-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 460px;
}

.table-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    margin: 0 0 5px;
    color: #263247;
    font-size: 19px;
  }

  p {
    margin: 0;
    color: #8a96a8;
    font-size: 12px;
  }
}

.table-actions {
  display: flex;
  gap: 10px;
}

.summary-table {
  flex: 1;
}

.detail-link {
  padding: 0;
  color: #2764f0;
  font: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.amount-negative {
  color: #ff5a5f;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 18px;
  color: #667387;
}

::v-deep .summary-tabs {
  .el-tabs__header {
    margin-bottom: 20px;
  }

  .el-tabs__item {
    color: #5a667a;
    font-size: 15px;
  }

  .el-tabs__item.is-active {
    color: #245eea;
  }
}

::v-deep .filter-panel {
  .el-input__inner {
    height: 40px;
    border-radius: 6px;
  }

  .el-select,
  .el-date-editor {
    width: 100%;
  }
}

::v-deep .summary-table {
  .el-table__header th {
    height: 50px;
    color: #40506a;
    background: #f7f9fc;
  }

  .el-table__body td {
    height: 54px;
  }

  .el-table__fixed-right::before,
  .el-table__fixed::before,
  &::before {
    background: #e2e7ef;
  }
}
</style>
